import { uiText } from '../i18n/uiText';
import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { CheckCircle2, AlertCircle, AlertTriangle, Info, X } from 'lucide-react';

/**
 * R14: глобальная Toast-система. Замена window.alert() — куда красивее.
 *
 * Использование:
 *   1. Обернуть App в <ToastProvider>...</ToastProvider> (один раз)
 *   2. В компоненте: const { showToast } = useToast();
 *      showToast({ type: 'success', message: 'Сохранено' });
 *      showToast({ type: 'error', message: 'Ошибка', detail: '...' });
 *
 * Типы: success | error | warning | info
 * Toast'ы автоматически исчезают через 4-6 секунд.
 */

const ToastContext = createContext(null);

let nextId = 1;

const TYPE_META = {
  success: { icon: CheckCircle2, color: '#15803d', bg: '#f0fdf4', border: '#86efac', timeout: 4000 },
  error:   { icon: AlertCircle,  color: '#b91c1c', bg: '#fef2f2', border: '#fca5a5', timeout: 6500 },
  warning: { icon: AlertTriangle, color: '#a16207', bg: '#fefce8', border: '#fde68a', timeout: 5000 },
  info:    { icon: Info,         color: '#1d4ed8', bg: '#eff6ff', border: '#bfdbfe', timeout: 4000 },
};

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const showToast = useCallback(({ type = 'info', message, detail }) => {
    const id = nextId++;
    const meta = TYPE_META[type] || TYPE_META.info;
    setToasts(prev => [...prev, { id, type, message, detail }]);
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, meta.timeout);
    return id;
  }, []);

  const dismissToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  useEffect(() => {
    const nativeAlert = window.alert;
    window.alert = (message) => showToast({
      type: 'info',
      message: String(message || uiText("Готово")),
    });
    return () => { window.alert = nativeAlert; };
  }, [showToast]);

  return (
    <ToastContext.Provider value={{ showToast, dismissToast }}>
      {children}
      <ToastContainer toasts={toasts} onDismiss={dismissToast}/>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    // Fallback — если провайдера нет, используем window.alert (defensive)
    return {
      showToast: ({ type, message }) => {
        const prefix = type === 'error' ? uiText("Ошибка: ") : type === 'success' ? uiText("Готово: ") : '';
        window.alert(prefix + message);
      },
      dismissToast: () => {},
    };
  }
  return ctx;
}

function ToastContainer({ toasts, onDismiss }) {
  return (
    <div className="app-toast-stack" aria-live="polite" aria-atomic="false">
      {toasts.map(t => (
        <ToastItem key={t.id} toast={t} onDismiss={() => onDismiss(t.id)} />
      ))}
    </div>
  );
}

function ToastItem({ toast, onDismiss }) {
  const meta = TYPE_META[toast.type] || TYPE_META.info;
  const Icon = meta.icon;
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 10);
    return () => clearTimeout(t);
  }, []);

  return (
    <div className="app-toast" role={toast.type === 'error' ? 'alert' : 'status'} style={{
      '--toast-color': meta.color, '--toast-bg': meta.bg, '--toast-border': meta.border,
      opacity: visible ? 1 : 0,
      transform: visible ? 'translateX(0)' : 'translateX(20px)',
    }}>
      <Icon size={18} style={{ color: meta.color, flexShrink: 0, marginTop: 1 }}/>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600, fontSize: 13, color: meta.color }}>
          {toast.message}
        </div>
        {toast.detail && (
          <div style={{ fontSize: 12, color: meta.color, opacity: 0.85, marginTop: 3 }}>
            {toast.detail}
          </div>
        )}
      </div>
      <button type="button" aria-label={uiText("Закрыть уведомление")} onClick={onDismiss} style={{
        background: 'transparent', border: 'none', padding: 0,
        cursor: 'pointer', color: meta.color, opacity: 0.5,
        display: 'flex', alignItems: 'center',
      }}>
        <X size={14}/>
      </button>
    </div>
  );
}
